import { useEffect, useMemo, useState } from 'react';
import { usePopper } from '@balance-web/popover';
import { themeRaw } from '@balance-web/theme';

import type { ComboboxLoadingState, ItemHeight } from './types';

// Menu height
// ------------------------------

const MAX_MENU_HEIGHT = 360;

/** Ensure that x items always fill the menu. */
export function useMaxMenuHeight(itemHeight: ItemHeight) {
  return useMemo(() => {
    if (typeof itemHeight === 'function') {
      return MAX_MENU_HEIGHT;
    }

    let maxMenuHeight = 0;

    for (let index = 8; index > 0; index--) {
      let attempt = itemHeight * index;
      if (attempt < MAX_MENU_HEIGHT) {
        maxMenuHeight = attempt;
        break;
      }
    }

    return maxMenuHeight;
  }, [itemHeight]);
}

// Item status
// ------------------------------

type GetItemsStatusProps = {
  inputValue: string | null;
  itemCount: number;
  loadingState: ComboboxLoadingState;
};
export function getItemsStatus({
  inputValue,
  itemCount,
  loadingState,
}: GetItemsStatusProps) {
  if (loadingState === 'error') {
    return 'error';
  }
  if (inputValue?.length && !itemCount) {
    return 'noMatches';
  }

  return null;
}

// Hooks
// ------------------------------

/** Tailor the popper handler for combobox. */
export function useComboboxPopper(isOpen: boolean) {
  // setup popper (position handling)
  const [anchorElement, popperAnchorRef] = useState<HTMLDivElement | null>(
    null
  );
  const [popoverElement, popperPopoverRef] = useState<HTMLDivElement | null>();

  const { styles, attributes, update } = usePopper(
    anchorElement,
    popoverElement,
    {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, themeRaw.spacing.small],
          },
        },
      ],
    }
  );

  // update popper when it opens to get the latest placement.
  // necessary for prerendered popovers in modals etc.
  useEffect(() => {
    if (update && isOpen) {
      update();
    }
  }, [isOpen, update]);

  return {
    attributes,
    styles,
    popperAnchorRef,
    popperPopoverRef,
  };
}
